import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import { withSessionHash } from '@/lib/authFetch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Wrench,
  AlertTriangle,
  FileText,
  FileCheck,
  Printer,
  MessageSquare,
  Mail,
  MapPin,
  Key,
  Pencil,
  ListPlus,
  CalendarClock,
  History,
  Package,
  PackageCheck,
  UserCog,
  TrendingDown,
  TrendingUp,
  NotebookPen,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import JobCardLineItems from './JobCardLineItems';
import JobCardSmsButton from './JobCardSmsButton';
import LabourSummaryPill from './LabourSummaryPill';
import JobCardLabourLive from './JobCardLabourLive';
import JobCardPerMechanicHours from './JobCardPerMechanicHours';
import MechanicFindingsList from './MechanicFindingsList';
import JobCardQuoteSection from './JobCardQuoteSection';
import JobCardStepIndicator from './JobCardStepIndicator';
import Step1VehicleInfo from './Step1VehicleInfo';
import Step4QualityChecklist from './Step4QualityChecklist';
import Step5WorkPerformed from './Step5WorkPerformed';
import QuickEditCustomerDialog from './QuickEditCustomerDialog';
import QuickEditVehicleDialog from './QuickEditVehicleDialog';
import { formatMelbourne, formatDeliveryDueLabel } from '@/lib/melbourneTime';
import { openInNewTab } from '@/lib/openInNewTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const STATUS_CONFIG = {
  quote: { label: 'Quote', color: 'bg-indigo-500', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
  booking: { label: 'Booking', color: 'bg-slate-400', bgColor: 'bg-slate-50', borderColor: 'border-slate-200' },
  no_show: { label: 'No Show', color: 'bg-amber-500', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  pickup: { label: 'Dropped-off', color: 'bg-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  waiting_for_parts: { label: 'Waiting for Parts', color: 'bg-orange-500', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
  blocked: { label: 'Sublet', color: 'bg-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  sm_review: { label: 'SM Review', color: 'bg-purple-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  completed: { label: 'Completed', color: 'bg-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' }
};

export const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600', icon: null },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700', icon: null },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700', icon: null },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

export const SERVICE_TYPE_LABELS = {
  service: 'Service',
  rwc: 'RWC',
  tyres: 'Tyres',
  aircon: 'Aircon',
  brakes: 'Brakes',
  suspension: 'Suspension',
  electrical: 'Electrical',
  other: 'Other'
};

export const CHECKLIST_ITEMS = [
  { key: 'repairs_checked', label: 'Repairs checked' },
  { key: 'brakes_checked', label: 'Brakes checked' },
  { key: 'tyres_checked', label: 'Tyres checked' },
  { key: 'wheel_alignment_checked', label: 'Wheel alignment checked' },
  { key: 'fluids_topped', label: 'Fluids topped' },
  { key: 'engine_oil_checked', label: 'Engine oil checked' }
];

export default function JobCardDetailModal({ jobCard, open, onOpenChange, user, mechanics, onUpdate, canManage }) {
  const [findings, setFindings] = useState(jobCard.findings || '');
  const [workPerformed, setWorkPerformed] = useState(jobCard.work_performed || '');
  const [hoursWorked, setHoursWorked] = useState(jobCard.hours_worked || '');
  const [actualHoursWorked, setActualHoursWorked] = useState(jobCard.actual_hours_worked || '');
  const [checklist, setChecklist] = useState(jobCard.quality_checklist || {});
  // iter138.6 — Single source-of-truth for "Next Service Date" is the
  // Vehicle Details field below (`nextSvcDate`). Quality Checklist used
  // to have a duplicate input that the user kept missing. We seed this
  // state from BOTH places (prefer the vehicle-details copy) and the
  // setter mirrors writes into `quality_checklist.next_service_date`
  // for backward compatibility with the SM Review payload.
  const [nextServiceDate, setNextServiceDate] = useState(
    jobCard.next_service_date
    || jobCard.quality_checklist?.next_service_date
    || ''
  );
  // Vehicle & service info the mechanic enters/corrects on the JC.
  // These mirror to the vehicle record on completion (handled server-side).
  const [vehRego, setVehRego] = useState(jobCard.vehicle_rego || '');
  const [vehMake, setVehMake] = useState(jobCard.vehicle_make || '');
  const [vehModel, setVehModel] = useState(jobCard.vehicle_model || '');
  const [vehYear, setVehYear] = useState(jobCard.vehicle_year || '');
  const [odometer, setOdometer] = useState(jobCard.odometer_km || '');
  const [serviceDate, setServiceDate] = useState(jobCard.service_date || '');
  // iter138.6 — Vehicle-details "Next Service Due" is now the ONE field
  // for next-service date (the Quality Checklist duplicate has been
  // removed). We seed from `next_service_date` first and fall through
  // to the legacy `quality_checklist.next_service_date` for older JCs.
  const [nextSvcDate, setNextSvcDateRaw] = useState(
    jobCard.next_service_date
    || jobCard.quality_checklist?.next_service_date
    || ''
  );
  // Custom setter — keeps the legacy `quality_checklist.next_service_date`
  // mirror in sync so submit-for-review's existing checklist validation
  // doesn't fire false positives on payloads written from the new UI.
  const setNextSvcDate = (val) => {
    setNextSvcDateRaw(val);
    setNextServiceDate(val);
  };
  const [saving, setSaving] = useState(false);
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState(jobCard.review_notes || '');

  // Quick-edit dialogs for customer / vehicle details (mirror of InvoiceEdit).
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  // Tracks the in-flight call for "Add customer concerns as tasks"
  const [addingConcerns, setAddingConcerns] = useState(false);
  // iter138.2 — SM/FM-authored notes editor. Local draft + dirty flag so
  // we can debounce-save with a status pill, without spamming PUTs while
  // the SM is still typing. Customer concerns become editable the same way.
  const [smNotesDraft, setSmNotesDraft] = useState(jobCard.sm_notes || '');
  const [smNotesSaving, setSmNotesSaving] = useState(false);
  const [smNotesSavedAt, setSmNotesSavedAt] = useState(null);

  // iter158 — Customer's most recent PRIOR invoice for reference. Fetched
  // on modal open so SM can click "View Last Invoice" and open the real
  // editable invoice page (not a PDF) — including Boyce-imported
  // historical invoices. Excludes the JC's own source_quote_invoice_id
  // so the SM lands on the invoice BEFORE the current booking's quote.
  const [lastInvoice, setLastInvoice] = useState(null);
  const [addingSmNotes, setAddingSmNotes] = useState(false);
  const [commentsDraft, setCommentsDraft] = useState(jobCard.comments || '');
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [commentsSavedAt, setCommentsSavedAt] = useState(null);
  // Reschedule visit date directly from the JC modal — SM doesn't need
  // to jump to the Bookings page when a customer calls to move the
  // appointment. `null` = closed, ISO string = inline form open.
  const [rescheduleValue, setRescheduleValue] = useState(null);
  const [reschedSaving, setReschedSaving] = useState(false);
  // Live-clock tick — bumps every 10s so the derived actual-hours pill
  // stays accurate while a mechanic is clocked in. Drives the memo
  // recompute below + the "Hh Mm so far · live" badge in Step 5.
  const [liveTick, setLiveTick] = useState(0);
  // For lead-mode JCs (no customer_id) we promote the lead inline before
  // opening the edit dialog. These IDs cache the resolved ids so we
  // don't re-promote on every Edit click.
  const [resolvedCustomerId, setResolvedCustomerId] = useState(null);
  const [resolvedVehicleId, setResolvedVehicleId] = useState(null);
  const [ensuringCustomer, setEnsuringCustomer] = useState(false);
  // iter138.9m — Fetch linked invoice details (doc_no, status, total, due)
  // so the modal can render a prominent "Linked Invoice" panel with
  // direct open-action. Without this, the SM saw "Invoice created" on
  // the kanban tile but had no visible affordance in the modal itself.
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [linkedInvoiceLoading, setLinkedInvoiceLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!jobCard.invoice_id) {
      setLinkedInvoice(null);
      return;
    }
    setLinkedInvoiceLoading(true);
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/invoices/${jobCard.invoice_id}`,
          { credentials: 'include' },
        );
        if (r.ok && !cancelled) setLinkedInvoice(await r.json());
      } catch (_e) { /* noop — UI just doesn't render the panel */ }
      finally { if (!cancelled) setLinkedInvoiceLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [jobCard.invoice_id]);
  
  const isMechanic = user && ['mechanic', 'technician'].includes(user.role);
  const isServiceManager = user && ['service_manager', 'floor_manager', 'admin', 'super_admin'].includes(user.role);
  const isAssigned = jobCard.assigned_to === user?.user_id;
  const canEdit = canManage || (isMechanic && isAssigned);
  const canDelete = user && user.role === 'super_admin';

  // Auto-derived hours from the labour-tracking subsystem. Sum quoted_labour_hours
  // across all tasks for the estimate; sum closed time_log sessions for actual.
  // If the operator hasn't manually overridden these (state is empty), the
  // input shows the auto-derived value; manual edits still take precedence.
  const derivedEstHours = useMemo(() => {
    const total = (jobCard.line_items || [])
      .reduce((s, li) => s + (Number(li.quoted_labour_hours) || 0), 0);
    return total > 0 ? total.toFixed(2) : '';
  }, [jobCard.line_items]);
  const derivedActualHours = useMemo(() => {
    const log = jobCard.time_log || [];
    let mins = 0;
    const now = Date.now();
    for (const s of log) {
      if (!s.started_at) continue;
      const start = new Date(s.started_at).getTime();
      const end = s.ended_at ? new Date(s.ended_at).getTime() : now;
      mins += Math.max(0, Math.floor((end - start) / 60000));
    }
    return mins > 0 ? (mins / 60).toFixed(2) : '';
    // `liveTick` forces a recompute every 10s while a session is open.
     
  }, [jobCard.time_log, liveTick]);

  // True iff the JC has an in-flight clock session (started_at set,
  // ended_at null). Drives the 10s tick + "live" badge.
  const hasOpenSession = useMemo(
    () => (jobCard.time_log || []).some(s => s.started_at && !s.ended_at),
    [jobCard.time_log],
  );

  // 10s tick — only runs while the modal is open AND there's an open
  // clock session. Cleans itself up so we don't leak intervals.
  useEffect(() => {
    if (!open || !hasOpenSession) return undefined;
    const id = setInterval(() => setLiveTick(t => t + 1), 10_000);
    return () => clearInterval(id);
  }, [open, hasOpenSession]);

  // iter138.2 — Keep the local notes drafts in sync with the server
  // payload when the JC reloads (onUpdate refresh, manual reload, etc).
  // We DON'T clobber unsaved edits — only refresh when local matches
  // the previously-loaded server value.
  useEffect(() => {
    setSmNotesDraft((cur) => (
      cur === '' || cur === (jobCard.sm_notes || '') ? (jobCard.sm_notes || '') : cur
    ));
  }, [jobCard.sm_notes]);
  useEffect(() => {
    setCommentsDraft((cur) => (
      cur === '' || cur === (jobCard.comments || '') ? (jobCard.comments || '') : cur
    ));
  }, [jobCard.comments]);

  // iter138.9q — HARD RESET on JC switch. Belt-and-suspenders to the
  // `key={jobCard.job_card_id}` prop on this modal (iter138.9f). User
  // reported "concerns from JC #A still appearing on JC #B" after the
  // remount fix shipped — likely a deep-link/race scenario where the
  // parent set `selectedJobCard` to the new JC but React hadn't yet
  // unmounted the old instance, OR a fork where the modal kept a stale
  // commentsDraft because the same-JC `updated_at` path was hit between
  // mount and the first useEffect run. This effect ALWAYS overwrites
  // the two draft fields from the prop the moment `job_card_id`
  // changes — no "don't clobber" guard since switching JCs always
  // discards in-flight unsaved edits on the previous one.
  useEffect(() => {
    setCommentsDraft(jobCard.comments || '');
    setSmNotesDraft(jobCard.sm_notes || '');
    setCommentsSavedAt(null);
    setSmNotesSavedAt(null);
     
  }, [jobCard.job_card_id]);

  // Effective hours sent on Save/Submit: prefer the manual input, then
  // fall back to the live-derived value so mechanic NEVER has to type
  // hours when the clock log has captured the work.
  const effectiveActualHours = () => {
    if (actualHoursWorked && parseFloat(actualHoursWorked) > 0) {
      return parseFloat(actualHoursWorked);
    }
    if (derivedActualHours && parseFloat(derivedActualHours) > 0) {
      return parseFloat(derivedActualHours);
    }
    return null;
  };
  
  // Check if job can be submitted for review.
  // `canSubmitForReview` (true gate) means everything checks out — used for
  // the green/ready styling. `showSubmitButton` is the looser visibility gate
  // — we ALWAYS show the button for an assigned mechanic on an active JC so
  // they have an obvious path to advance the JC. Click-handler runs the
  // detailed pre-checks and toasts contextual hints when something's missing.
  const checklistComplete = CHECKLIST_ITEMS.every(item => checklist[item.key]) && nextServiceDate;
  const canSubmitForReview = checklistComplete && 
    ['in_progress', 'waiting_for_parts', 'blocked'].includes(jobCard.status) &&
    (isMechanic && isAssigned || isServiceManager);
  const showSubmitButton = 
    ['in_progress', 'waiting_for_parts', 'blocked'].includes(jobCard.status) &&
    ((isMechanic && isAssigned) || isServiceManager);
  
  // Check if SM can review this job
  const canSMReview = jobCard.status === 'sm_review' && isServiceManager;

  // Per-step completion (drives the sticky JobCardStepIndicator pills).
  // Each rule is intentionally lightweight — a checkmark just says "this
  // step has at least some real input"; not a strict gate.
  const stepCompletion = useMemo(() => ({
    1: !!(vehRego && vehMake && vehModel),                           // Vehicle Info
    2: ((jobCard.line_items || []).some(li => li.completed)),         // At least one task ticked
    3: ((jobCard.findings_items?.length || 0) > 0)
        || !!(jobCard.findings && jobCard.findings.trim()),           // Findings recorded
    4: checklistComplete,                                             // QC + next-service date
    5: !!(workPerformed && workPerformed.trim())
        && parseFloat(actualHoursWorked || '0') > 0,                  // Narrative + actual hrs
  }), [vehRego, vehMake, vehModel, jobCard.line_items, jobCard.findings_items, jobCard.findings, checklistComplete, workPerformed, actualHoursWorked]);
  
  // Update local state when jobCard changes.
  //
  // Two distinct triggers MUST be handled differently:
  //
  //   (a) SM/mechanic switches to a different JC (job_card_id changes)
  //       — FULL RESET. Discard any unsaved local edits since they
  //       belong to the previous JC.
  //
  //   (b) The SAME JC is re-fetched (job_card_id stable, updated_at
  //       bumped). This happens when:
  //         • SM saves QuickEdit Vehicle / QuickEdit Customer dialog
  //         • Mechanic adds a finding (POST /findings push)
  //         • A photo upload completes
  //         • A drag-drop status change confirms
  //       In ALL of these the modal stays open and the mechanic may
  //       have UNSAVED text in odometer / next-service-date / findings
  //       / work-performed / hours-worked. DON'T-CLOBBER PATTERN:
  //       sync a field from the new prop ONLY IF the local value
  //       still equals the previously-seen server value (i.e. no
  //       unsaved edit). This is what fixes the user's report:
  //         "mechanic entered odometer and next service date, then
  //          added a finding, odometer + next service date disappeared".
  const lastSeenServerRef = useRef(null);

  // ── (a) JC switch — full reset
  useEffect(() => {
    const seed = {
      findings: jobCard.findings || '',
      work_performed: jobCard.work_performed || '',
      hours_worked: jobCard.hours_worked || '',
      actual_hours_worked: jobCard.actual_hours_worked || '',
      quality_checklist: jobCard.quality_checklist || {},
      quality_next_service: jobCard.quality_checklist?.next_service_date || '',
      review_notes: jobCard.review_notes || '',
      vehicle_rego: jobCard.vehicle_rego || '',
      vehicle_make: jobCard.vehicle_make || '',
      vehicle_model: jobCard.vehicle_model || '',
      vehicle_year: jobCard.vehicle_year || '',
      odometer_km: jobCard.odometer_km == null ? '' : String(jobCard.odometer_km),
      service_date: jobCard.service_date || '',
      next_service_date: jobCard.next_service_date || '',
    };
    setFindings(seed.findings);
    setWorkPerformed(seed.work_performed);
    setHoursWorked(seed.hours_worked);
    setActualHoursWorked(seed.actual_hours_worked);
    setChecklist(seed.quality_checklist);
    setNextServiceDate(seed.quality_next_service);
    setReviewNotes(seed.review_notes);
    setVehRego(seed.vehicle_rego);
    setVehMake(seed.vehicle_make);
    setVehModel(seed.vehicle_model);
    setVehYear(seed.vehicle_year);
    setOdometer(seed.odometer_km);
    setServiceDate(seed.service_date);
    setNextSvcDate(seed.next_service_date);
    setResolvedCustomerId(null);
    setResolvedVehicleId(null);
    setShowEditCustomer(false);
    setShowEditVehicle(false);
    setLastInvoice(null); // iter158 — reset when JC switches
    lastSeenServerRef.current = seed;
     
  }, [jobCard.job_card_id]);

  // iter158 — Fetch the customer's most recent PRIOR invoice for the
  // "View Last Invoice" reference link. Excludes any invoice_id already
  // linked to this JC (source_quote_invoice_id) so the SM lands on the
  // invoice from BEFORE this booking's quote — which is what "last
  // invoice for reference" means to the workshop.
  //
  // Deps are extracted to primitives BEFORE the useEffect so parent
  // re-renders that hand us a new `jobCard` object identity (but the
  // same underlying values) don't fire a second identical fetch.
  const _jcCustomerIdForFetch = jobCard.customer_id || null;
  const _jcSourceQuoteIdForFetch = jobCard.source_quote_invoice_id || null;
  useEffect(() => {
    if (!_jcCustomerIdForFetch) {
      setLastInvoice(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (_jcSourceQuoteIdForFetch) {
          params.set('exclude_invoice_id', _jcSourceQuoteIdForFetch);
        }
        const url = `${BACKEND_URL}/api/customers/${_jcCustomerIdForFetch}/last-invoice`
          + (params.toString() ? `?${params.toString()}` : '');
        const res = await fetch(url, { credentials: 'include' });
        if (cancelled) return;
        if (res.status === 204) {
          setLastInvoice(null);   // No prior invoice on file
          return;
        }
        if (!res.ok) return;
        const doc = await res.json();
        setLastInvoice(doc);
      } catch (_e) { /* silent — the link just doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [_jcCustomerIdForFetch, _jcSourceQuoteIdForFetch]);

  // ── (b) Same JC refresh — don't-clobber unsaved edits
  useEffect(() => {
    if (!lastSeenServerRef.current) return;  // initial mount handled by (a)
    const old = lastSeenServerRef.current;
    const norm = (v) => (v === null || v === undefined ? '' : String(v));

    const safeSet = (cur, oldServ, newServ, setter) => {
      // sync ONLY when local matches the previously-seen server value
      // (i.e. mechanic has no unsaved edit on this field).
      if (norm(cur) === norm(oldServ)) {
        setter(newServ);
      }
    };

    setFindings(cur => norm(cur) === norm(old.findings) ? (jobCard.findings || '') : cur);
    setWorkPerformed(cur => norm(cur) === norm(old.work_performed) ? (jobCard.work_performed || '') : cur);
    setHoursWorked(cur => norm(cur) === norm(old.hours_worked) ? (jobCard.hours_worked || '') : cur);
    setActualHoursWorked(cur => norm(cur) === norm(old.actual_hours_worked) ? (jobCard.actual_hours_worked || '') : cur);
    setReviewNotes(cur => norm(cur) === norm(old.review_notes) ? (jobCard.review_notes || '') : cur);
    setVehRego(cur => norm(cur) === norm(old.vehicle_rego) ? (jobCard.vehicle_rego || '') : cur);
    setVehMake(cur => norm(cur) === norm(old.vehicle_make) ? (jobCard.vehicle_make || '') : cur);
    setVehModel(cur => norm(cur) === norm(old.vehicle_model) ? (jobCard.vehicle_model || '') : cur);
    setVehYear(cur => norm(cur) === norm(old.vehicle_year) ? (jobCard.vehicle_year || '') : cur);
    setOdometer(cur => {
      const newServ = jobCard.odometer_km == null ? '' : String(jobCard.odometer_km);
      return norm(cur) === norm(old.odometer_km) ? newServ : cur;
    });
    setServiceDate(cur => norm(cur) === norm(old.service_date) ? (jobCard.service_date || '') : cur);
    setNextSvcDate(cur => norm(cur) === norm(old.next_service_date) ? (jobCard.next_service_date || '') : cur);
    setNextServiceDate(cur => {
      const newServ = jobCard.quality_checklist?.next_service_date || '';
      return norm(cur) === norm(old.quality_next_service) ? newServ : cur;
    });

    // Update last-seen to the fresh prop snapshot for the next refresh.
    lastSeenServerRef.current = {
      findings: jobCard.findings || '',
      work_performed: jobCard.work_performed || '',
      hours_worked: jobCard.hours_worked || '',
      actual_hours_worked: jobCard.actual_hours_worked || '',
      quality_checklist: jobCard.quality_checklist || {},
      quality_next_service: jobCard.quality_checklist?.next_service_date || '',
      review_notes: jobCard.review_notes || '',
      vehicle_rego: jobCard.vehicle_rego || '',
      vehicle_make: jobCard.vehicle_make || '',
      vehicle_model: jobCard.vehicle_model || '',
      vehicle_year: jobCard.vehicle_year || '',
      odometer_km: jobCard.odometer_km == null ? '' : String(jobCard.odometer_km),
      service_date: jobCard.service_date || '',
      next_service_date: jobCard.next_service_date || '',
    };
     
  }, [jobCard.updated_at]);
  
  const handleSaveMechanicWork = async () => {
    setSaving(true);
    try {
      // ── DIRTY-FIELD GUARD ─────────────────────────────────────────────
      // PROD recurrence (Jun 2026): SM edited Vehicle/Customer via the
      // dedicated dialogs which correctly synced the JC snapshot, but the
      // SAME mounted modal still held the OLD vehRego/Make/Model in local
      // state for the briefest moment (between save → refetch →
      // setSelectedJobCard → useEffect reseed). If SM clicked "Save Work
      // Details" inside that ~150-200ms window, the PUT sent the stale
      // values and overwrote the just-correct DB snapshot.
      //
      // Defence in depth: only include vehicle/odometer/service-date
      // fields in the PUT payload when they ACTUALLY differ from the
      // current prop (= last fetched DB state). Work fields (findings,
      // work_performed, hours_worked) are ALWAYS sent because they're
      // the explicit point of this button.
      const payload = {
        findings: findings || null,
        work_performed: workPerformed || null,
        hours_worked: hoursWorked ? parseFloat(hoursWorked) : null,
        actual_hours_worked: effectiveActualHours(),
      };
      const norm = (v) => (v === null || v === undefined || v === '') ? '' : String(v);
      const propRego = (jobCard.vehicle_rego || '').toUpperCase();
      const localRego = (vehRego || '').toUpperCase();
      if (localRego !== propRego) payload.vehicle_rego = localRego || null;
      if (norm(vehMake) !== norm(jobCard.vehicle_make)) payload.vehicle_make = vehMake || null;
      if (norm(vehModel) !== norm(jobCard.vehicle_model)) payload.vehicle_model = vehModel || null;
      if (norm(vehYear) !== norm(jobCard.vehicle_year)) {
        payload.vehicle_year = vehYear ? parseInt(vehYear, 10) : null;
      }
      if (norm(odometer) !== norm(jobCard.odometer_km)) {
        payload.odometer_km = odometer ? parseInt(odometer, 10) : null;
      }
      if (norm(serviceDate) !== norm(jobCard.service_date)) {
        payload.service_date = serviceDate || null;
      }
      if (norm(nextSvcDate) !== norm(jobCard.next_service_date)) {
        payload.next_service_date = nextSvcDate || null;
      }
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/mechanic-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('Work details saved');
        onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save work details');
    } finally {
      setSaving(false);
    }
  };

  /* iter164 — Mechanic-toggled Ready-to-Quote flag. Idempotent POST.
     Server clears the flag automatically on invoice creation, so the
     tile pill flips orange → emerald without any extra roundtrip. */
  const [readyToQuoteBusy, setReadyToQuoteBusy] = useState(false);
  const handleToggleReadyToQuote = async () => {
    if (readyToQuoteBusy) return;
    setReadyToQuoteBusy(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/mark-ready-to-quote`,
        { method: 'POST', credentials: 'include' },
      );
      if (r.ok) {
        const data = await r.json().catch(() => ({}));
        toast.success(
          data.ready_to_quote
            ? 'Marked Ready to Quote — SM has been notified'
            : 'Ready-to-Quote flag cleared'
        );
        onUpdate();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(typeof err?.detail === 'string' ? err.detail : 'Could not update Ready-to-Quote flag');
      }
    } catch (_e) {
      toast.error('Network error toggling Ready to Quote');
    } finally {
      setReadyToQuoteBusy(false);
    }
  };
  
  const handleSubmitForReview = async () => {
    // Frontend pre-checks — show clear, contextual hints BEFORE hitting the
    // API so the mechanic isn't bouncing back & forth with the backend.
    // Each gate fires its own toast so we never collapse multiple distinct
    // problems into a single confusing message.

    // 1. Odometer + rego — set on the JC body, NOT the checklist. Use the
    // LOCAL editable state so a value the mechanic has typed but not yet
    // saved still passes the pre-check (they can save + submit in one go).
    const missingTop = [];
    if (!vehRego || !String(vehRego).trim()) missingTop.push('Vehicle rego');
    if (odometer === null || odometer === undefined || odometer === '' || Number(odometer) === 0) {
      missingTop.push('Odometer reading (km)');
    }
    if (missingTop.length) {
      toast.error(
        `Please record ${missingTop.join(' + ')} at the top of the job card (Step 1 — Vehicle Inspection) before submitting.`,
        { duration: 10000 },
      );
      return;
    }

    // 2. All assigned tasks must be ticked Done (Step 2).
    const pendingTasks = (jobCard.line_items || []).filter(li => !li.completed);
    if (pendingTasks.length > 0) {
      const sample = pendingTasks
        .slice(0, 3)
        .map(li => li.description || li.line_id || 'task')
        .join(', ');
      const more = pendingTasks.length > 3 ? `, and ${pendingTasks.length - 3} more` : '';
      toast.error(
        `${pendingTasks.length} task${pendingTasks.length === 1 ? '' : 's'} still pending — tick them Done in Step 2 (${sample}${more}). Or use "Schedule follow-up JC" to move them off this card.`,
        { duration: 10000 },
      );
      return;
    }

    // 3. Quality checklist (Step 4) — list EXACTLY what's missing so the
    // mechanic doesn't have to hunt for it. iter138.6 — next-service-date
    // now lives in Step 1 (Vehicle Details) ONLY; the duplicate in Step 4
    // was removed because mechanics kept filling one and missing the other.
    const missingChecklist = CHECKLIST_ITEMS.filter(item => !checklist[item.key]).map(i => i.label);
    const stepHints = missingChecklist.length ? ['Step 4 (Quality Checklist) — ' + missingChecklist.join(', ')] : [];
    if (!nextSvcDate && !nextServiceDate) {
      stepHints.push('Step 1 (Vehicle Details) — Next Service Due date');
    }
    if (stepHints.length) {
      toast.error(
        `Almost there — please complete: ${stepHints.join(' · ')}.`,
        { duration: 10000 },
      );
      return;
    }

    setSubmittingForReview(true);
    try {
      // Submit with the effective actual hours: manual input if typed,
      // otherwise the live-derived value from the clock log. BE still
      // has its own auto-fill safety net if both are empty.
      const hoursPayload = effectiveActualHours();
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/submit-for-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          actual_hours_worked: hoursPayload,
          findings: findings || null,
          work_performed: workPerformed || null,
        }),
      });

      if (response.ok) {
        toast.success('Job submitted for Service Manager review');
        onUpdate();
        onOpenChange(false);
      } else {
        const error = await response.json().catch(() => ({}));
        // Surface the BE's reason — prefer plain-string detail (our app's
        // contextual messages), then fall back to FastAPI/Pydantic's
        // structured validation array. Long-duration toast so the
        // mechanic has time to read on the workshop floor.
        let beMessage = null;
        if (typeof error?.detail === 'string') {
          beMessage = error.detail;
        } else if (Array.isArray(error?.detail) && error.detail.length > 0) {
          // Pydantic 422 — show the first validation message in plain words.
          const firstField = error.detail[0]?.loc?.[error.detail[0].loc.length - 1] || 'field';
          beMessage = `Validation error on ${firstField}: ${error.detail[0]?.msg || 'invalid'}.`;
        }
        toast.error(
          beMessage || 'Failed to submit for review — please refresh and try again.',
          { duration: 10000 },
        );
      }
    } catch (_e) {
      toast.error('Network error — please check your connection and try again.', { duration: 8000 });
    } finally {
      setSubmittingForReview(false);
    }
  };
  
  const handleSMReview = async (action) => {
    setReviewAction(action);
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/sm-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: action,
          actual_hours_worked: effectiveActualHours(),
          review_notes: reviewNotes || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onUpdate();
        onOpenChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to complete review');
      }
    } catch (error) {
      toast.error('Failed to complete review');
    } finally {
      setReviewAction('');
    }
  };
  
  const handleChecklistChange = async (key, value) => {
    const newChecklist = { ...checklist, [key]: value };
    setChecklist(newChecklist);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/quality-checklist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
      
      if (!response.ok) {
        // Revert on failure
        setChecklist(checklist);
        toast.error('Failed to update checklist');
      }
    } catch (error) {
      setChecklist(checklist);
      toast.error('Failed to update checklist');
    }
  };
  
  const handleNextServiceDateChange = async (date) => {
    setNextServiceDate(date);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/quality-checklist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ next_service_date: date || null })
      });
      
      if (response.ok) {
        onUpdate();
      } else {
        toast.error('Failed to update next service date');
      }
    } catch (error) {
      toast.error('Failed to update next service date');
    }
  };
  
  // Photos are now per-finding (see MechanicFindingsList) — the old
  // job-card-level photo tab + handlers were removed.

  // Convert each line of the customer concerns block into its own JC task.
  // Idempotent server-side — pressing twice doesn't duplicate. SM uses
  // this on legacy JCs that came in before iter110's auto-explode.
  const handleAddConcernsAsTasks = async () => {
    setAddingConcerns(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/add-concerns-as-tasks`,
        { method: 'POST', credentials: 'include' },
      );
      if (r.ok) {
        toast.success('Customer concerns added as tasks');
        onUpdate();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not add concerns as tasks');
      }
    } catch (_e) {
      toast.error('Could not add concerns as tasks');
    } finally {
      setAddingConcerns(false);
    }
  };

  // iter138.2 — Persist the SM/FM-authored notes block. The Tasks section
  // pulls these on demand via the "Add as tasks" button (mirrors the
  // Customer Concerns idempotent flow). Saved via the generic JC PUT.
  const saveSmNotes = async () => {
    if (smNotesSaving) return;
    if ((smNotesDraft || '') === (jobCard.sm_notes || '')) return;
    // iter138.9u — snapshot JC id at call-time to defend against prop swaps.
    const targetJcId = jobCard.job_card_id;
    const draftAtSave = smNotesDraft || '';
    setSmNotesSaving(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${targetJcId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sm_notes: draftAtSave }),
        },
      );
      if (r.ok) {
        if (jobCard.job_card_id === targetJcId) {
          setSmNotesSavedAt(new Date());
        }
        onUpdate?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not save notes');
      }
    } catch (_e) {
      toast.error('Could not save notes');
    } finally {
      setSmNotesSaving(false);
    }
  };

  // Persist the editable Customer Concerns block. SM/FM only — fixes typos
  // captured at check-in / from the booking. Mechanic sees the saved
  // version on their iPad in real-time after the next refresh.
  const saveComments = async () => {
    if (commentsSaving) return;
    if ((commentsDraft || '') === (jobCard.comments || '')) return;
    // iter138.9u — capture the JC id AT THE INSTANT THE SAVE IS REQUESTED.
    // If `jobCard` is somehow replaced mid-flight (e.g. parent flipped
    // selectedJobCard between blur firing and fetch resolving), the
    // closure's captured `targetJcId` keeps the write scoped to the JC
    // the user actually edited. User reported: "i saved job card and
    // customer concern still overwriting other job cards customer
    // concern" — this defends against the prop-swap race even though
    // the existing closure already captures the right id.
    const targetJcId = jobCard.job_card_id;
    const draftAtSave = commentsDraft || '';
    setCommentsSaving(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${targetJcId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ comments: draftAtSave }),
        },
      );
      if (r.ok) {
        // Only surface "Saved" if the modal is STILL showing the same
        // JC — otherwise the saved indicator would land on the wrong
        // JC's UI.
        if (jobCard.job_card_id === targetJcId) {
          setCommentsSavedAt(new Date());
        }
        onUpdate?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not save concerns');
      }
    } catch (_e) {
      toast.error('Could not save concerns');
    } finally {
      setCommentsSaving(false);
    }
  };

  const handleAddSmNotesAsTasks = async () => {
    setAddingSmNotes(true);
    try {
      // Make sure unsaved edits are persisted before promoting to tasks
      if ((smNotesDraft || '') !== (jobCard.sm_notes || '')) await saveSmNotes();
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/add-sm-notes-as-tasks`,
        { method: 'POST', credentials: 'include' },
      );
      if (r.ok) {
        toast.success('SM/FM notes added as tasks');
        onUpdate();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not add notes as tasks');
      }
    } catch (_e) {
      toast.error('Could not add notes as tasks');
    } finally {
      setAddingSmNotes(false);
    }
  };

  // Open the customer/vehicle edit dialog from the JC modal. For
  // lead-mode JCs (no customer_id) this first promotes the lead to a
  // real customer via /ensure-customer so the existing edit dialogs
  // (which expect a customer_id) work transparently. SM never has to
  // know whether the JC was lead-mode or full-customer.
  const openEditDialog = async (target /* 'customer' | 'vehicle' */) => {
    // Fast path — we already have ids.
    if (target === 'customer' && jobCard.customer_id) {
      setResolvedCustomerId(jobCard.customer_id);
      setShowEditCustomer(true);
      return;
    }
    if (target === 'vehicle' && jobCard.vehicle_id) {
      setResolvedVehicleId(jobCard.vehicle_id);
      setShowEditVehicle(true);
      return;
    }
    // Need to promote the lead first. Cache result on the modal state so
    // a follow-up Edit click is instant.
    if (resolvedCustomerId && target === 'customer') {
      setShowEditCustomer(true);
      return;
    }
    if (resolvedVehicleId && target === 'vehicle') {
      setShowEditVehicle(true);
      return;
    }
    setEnsuringCustomer(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/ensure-customer`,
        { method: 'POST', credentials: 'include' },
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not load customer details — please try again.');
        return;
      }
      const d = await r.json();
      setResolvedCustomerId(d.customer_id);
      setResolvedVehicleId(d.vehicle_id);
      if (d.promoted) {
        toast.success('Lead promoted to customer — you can now edit details.');
        onUpdate?.();  // refresh JC payload so customer_id is visible
      }
      if (target === 'customer') {
        setShowEditCustomer(true);
      } else if (target === 'vehicle') {
        // ensure-customer now creates the vehicle on-the-fly from the
        // JC's snapshot if none exists — so this branch should always
        // receive a vehicle_id. Defensive null-check left in for
        // belt-and-braces but no longer the common path.
        if (!d.vehicle_id) {
          toast.error('Could not register vehicle — please re-save the booking with make/model.');
          return;
        }
        setShowEditVehicle(true);
      }
    } catch (_e) {
      toast.error('Network error — could not load customer details.');
    } finally {
      setEnsuringCustomer(false);
    }
  };

  // Reschedule the visit from inside the JC modal. Saves to the JC AND
  // mirrors to the linked booking doc server-side so the Bookings page
  // stays in sync.
  const handleSaveReschedule = async () => {
    if (!rescheduleValue) {
      toast.error('Pick a new date and time first.');
      return;
    }
    setReschedSaving(true);
    try {
      // datetime-local inputs return wall-clock with no zone — send as-is
      // so the backend treats it as Melbourne local (matches booking-create).
      const r = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ booked_datetime: rescheduleValue }),
      });
      if (r.ok) {
        const wasQuote = jobCard.status === 'quote';
        toast.success(
          wasQuote
            ? 'Quote scheduled — promoted to Booking and added to Bookings calendar.'
            : 'Booking rescheduled — Bookings calendar also updated.',
        );
        setRescheduleValue(null);
        onUpdate();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(
          (typeof e?.detail === 'string' ? e.detail : null)
            || 'Could not reschedule — please try again.',
        );
      }
    } catch (_e) {
      toast.error('Network error — could not reschedule.');
    } finally {
      setReschedSaving(false);
    }
  };

  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[92vw] sm:!max-w-[88vw] w-[92vw] sm:w-[88vw] !max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3 flex-wrap">
              <span className="text-slate-900 font-bold text-xl tracking-wide">{formatRego(jobCard.vehicle_rego) || 'NO REGO'}</span>
              <Badge className={STATUS_CONFIG[jobCard.status]?.bgColor}>
                {STATUS_CONFIG[jobCard.status]?.label}
              </Badge>
              {/* iter138.9v — Delivery Due prominently on top.
                  SM/FM sets it from the JC tile 3-dot menu; the
                  mechanic ALWAYS knows the hard departure deadline
                  the moment they open the modal. Amber chip so it
                  stands out against the status badge. */}
              {jobCard.delivery_due_at && (() => {
                const label = formatDeliveryDueLabel(jobCard.delivery_due_at);
                if (!label) return null;
                return (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2.5 py-1"
                    data-testid="jc-modal-delivery-due"
                    title="Delivery due — when the car must leave the workshop"
                  >
                    <CalendarClock className="h-3 w-3" />
                    DD By {label}
                  </span>
                );
              })()}
              {/* Productivity pill — visible to SM / admin / super_admin only. */}
              {!isMechanic && (
                <LabourSummaryPill jobCardId={jobCard.job_card_id} refreshKey={jobCard.updated_at} />
              )}
              <JobCardLabourLive card={jobCard} />
              {/* iter138.9y — Per-mechanic breakdown when 2+ mechanics
                  contributed (Option B from user). Shows each mechanic's
                  clocked hours so SM/FM can see who did what before a
                  reassignment. */}
              <JobCardPerMechanicHours card={jobCard} mechanics={mechanics || []} />
            </DialogTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(withSessionHash(`/job-cards/print/${jobCard.job_card_id}`), '_blank')}
              className="mr-2"
            >
              <Printer className="h-4 w-4 mr-1" />
              Print
            </Button>
            {/* SMS quick-send — SM / SA only. Mechanics never see this button.
                Falls back to dry-run mode automatically if CELLCAST_APPKEY
                is not configured (handled inside the component). */}
            <JobCardSmsButton jobCard={jobCard} user={user} />
          </div>
          <DialogDescription>
            <span className="text-blue-600 font-medium">{jobCard.job_card_number}</span> • {jobCard.customer_name} • {SERVICE_TYPE_LABELS[jobCard.service_type]}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-5">
          {/* Sticky 5-step indicator strip — clickable pills, checkmarks
              once each step has real input. Optimised for iPad floor use. */}
          <JobCardStepIndicator completion={stepCompletion} />

          {/* iter138.9m — Prominent Linked Invoice panel. Shows up
              whenever this JC has been billed, regardless of role —
              mechanic + SM + FM + Super Admin all see it. Surfaces
              doc_no / status / total / due date and a clear "Open
              Invoice" button. Fixes the user-reported issue where the
              kanban tile said "Invoice created" but the modal had no
              visible way to find it. */}
          {jobCard.invoice_id && (
            <div
              className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/60"
              data-testid="jc-linked-invoice-panel"
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                  <FileCheck className="h-4 w-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider font-semibold text-emerald-900 mb-0.5">Linked Invoice</p>
                  {linkedInvoiceLoading ? (
                    <p className="text-sm text-emerald-700">Loading invoice details…</p>
                  ) : linkedInvoice ? (
                    <div className="flex items-center gap-2 flex-wrap" data-testid="jc-linked-invoice-summary">
                      <span className="text-sm font-bold text-slate-900" data-testid="jc-linked-invoice-doc-no">
                        {linkedInvoice.doc_no || 'Invoice'}
                      </span>
                      <span className="text-sm text-slate-700 tabular-nums">
                        ${(linkedInvoice.total || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                      </span>
                      {/* Status badge */}
                      {(() => {
                        const s = (linkedInvoice.status || 'draft').toLowerCase();
                        const cls = s === 'paid'
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : s === 'overdue'
                          ? 'bg-rose-100 text-rose-800 border-rose-300'
                          : s === 'sent' || s === 'finalized'
                          ? 'bg-blue-100 text-blue-800 border-blue-300'
                          : 'bg-slate-100 text-slate-700 border-slate-300';
                        return (
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wide border rounded-full px-2 py-0.5 ${cls}`}
                            data-testid="jc-linked-invoice-status"
                          >
                            {s}
                          </span>
                        );
                      })()}
                      {linkedInvoice.due_date && (
                        <span className="text-xs text-slate-500">
                          Due {new Date(linkedInvoice.due_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">Invoice exists but details couldn't be loaded.</p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">
                    Invoice ID <span className="font-mono">{jobCard.invoice_id}</span>
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => openInNewTab(`/invoices/${jobCard.invoice_id}`)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="jc-open-linked-invoice-btn"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Open Invoice
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Customer details — read-only at the top so mechanic & SM always
              see who/what they're dealing with. SM/admin can quick-edit
              from inside the modal via the small pencil links so old
              customers who can't type at the kiosk get their details
              captured properly at drop-off. */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="flex items-center gap-2 text-slate-500 mb-0.5">
                <span>Customer:</span>
                {canManage && (jobCard.customer_id || jobCard.lead_id) && (
                  <button
                    type="button"
                    onClick={() => openEditDialog('customer')}
                    disabled={ensuringCustomer}
                    className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                    data-testid="jc-edit-customer-details-btn"
                  >
                    <Pencil className="h-3 w-3" />
                    {ensuringCustomer ? 'Loading…' : 'Edit customer details'}
                  </button>
                )}
              </div>
              <p className="font-medium" data-testid="jc-customer-name">{jobCard.customer_name || '-'}</p>
            </div>
            <div>
              <span className="text-slate-500">Phone:</span>
              <p className="font-medium" data-testid="jc-customer-phone">{formatAUMobile(jobCard.customer_phone) || '-'}</p>
            </div>
            <div>
              <span className="text-slate-500 flex items-center gap-1"><Mail className="h-3 w-3" />Email:</span>
              <p className="font-medium break-all" data-testid="jc-customer-email">{jobCard.customer_email || '-'}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-500 mb-0.5">
                <span>Vehicle:</span>
                {canManage && (jobCard.vehicle_id || jobCard.lead_id || jobCard.customer_id) && (
                  <button
                    type="button"
                    onClick={() => openEditDialog('vehicle')}
                    disabled={ensuringCustomer}
                    className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                    data-testid="jc-edit-vehicle-details-btn"
                  >
                    <Pencil className="h-3 w-3" />
                    {ensuringCustomer ? 'Loading…' : 'Edit vehicle details'}
                  </button>
                )}
              </div>
              <p className="font-medium">{jobCard.vehicle_rego} {jobCard.vehicle_make} {jobCard.vehicle_model}</p>
            </div>
            <div>
              <span className="text-slate-500">Assigned To:</span>
              <p className="font-medium">{jobCard.assigned_mechanic_name || 'Unassigned'}</p>
            </div>
            <div data-testid="jc-created-at">
              <span className="text-slate-500">Created:</span>
              <p className="font-medium">
                {jobCard.created_at
                  ? formatMelbourne(jobCard.created_at, {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })
                  : '-'}
              </p>
            </div>
            {/* Booked-for date — SM can reschedule inline so they don't
                need to jump to the Bookings page. Save propagates to the
                linked booking doc server-side. Mechanics see read-only.
                Hidden entirely for completed/cancelled JCs.
                iter139.1 — Also rendered for quote-status JCs (which have
                no booked_datetime yet) so the SM can SCHEDULE the quote
                directly from the kanban tile — submitting the datetime
                promotes the quote → real booking server-side. */}
            {(jobCard.booked_datetime || jobCard.status === 'quote') && (
              <div data-testid="jc-booked-for">
                <span className="text-slate-500 flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" />
                  {jobCard.status === 'quote' && !jobCard.booked_datetime
                    ? 'Schedule this quote:'
                    : 'Booked for:'}
                </span>
                {rescheduleValue === null ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {jobCard.booked_datetime ? (
                      <p className="font-medium" data-testid="jc-booked-datetime-display">
                        {formatMelbourne(jobCard.booked_datetime, {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    ) : (
                      <p className="text-xs italic text-slate-500" data-testid="jc-booked-datetime-display">
                        Not scheduled yet — pick a date to convert this quote into a booking.
                      </p>
                    )}
                    {canManage && !['completed', 'cancelled'].includes(jobCard.status) && (
                      <button
                        type="button"
                        onClick={() => {
                          // Pre-fill the datetime-local input with the
                          // existing booked_datetime in Melbourne wall-clock.
                          // datetime-local needs "YYYY-MM-DDTHH:mm" with no zone.
                          // iter139.1 — For quote JCs with no booked_datetime,
                          // seed with TODAY at 09:00 Melbourne so SM has a
                          // sensible default.
                          try {
                            const src = jobCard.booked_datetime || new Date().toISOString();
                            const d = new Date(src);
                            const parts = new Intl.DateTimeFormat('en-CA', {
                              timeZone: 'Australia/Melbourne',
                              year: 'numeric', month: '2-digit', day: '2-digit',
                              hour: '2-digit', minute: '2-digit', hour12: false,
                            }).formatToParts(d).reduce((acc, p) => {
                              if (p.type !== 'literal') acc[p.type] = p.value;
                              return acc;
                            }, {});
                            const hh = parts.hour === '24' ? '00' : parts.hour;
                            const useDefaults = !jobCard.booked_datetime;
                            setRescheduleValue(
                              `${parts.year}-${parts.month}-${parts.day}T${useDefaults ? '09:00' : `${hh}:${parts.minute}`}`,
                            );
                          } catch {
                            setRescheduleValue('');
                          }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid="jc-reschedule-btn"
                      >
                        <Pencil className="h-3 w-3" />
                        {jobCard.status === 'quote' && !jobCard.booked_datetime ? 'Schedule' : 'Reschedule'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <input
                      type="datetime-local"
                      value={rescheduleValue}
                      onChange={(e) => setRescheduleValue(e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid="jc-reschedule-input"
                      disabled={reschedSaving}
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveReschedule}
                      disabled={reschedSaving || !rescheduleValue}
                      data-testid="jc-reschedule-save-btn"
                      className="h-8"
                    >
                      {reschedSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRescheduleValue(null)}
                      disabled={reschedSaving}
                      data-testid="jc-reschedule-cancel-btn"
                      className="h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {rescheduleValue !== null && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Saves to the job card AND the customer's booking record.
                  </p>
                )}
              </div>
            )}
            {/* iter216 — Driver's licence (keyed at the kiosk) + address sit
                right after "Booked for" per owner request. */}
            <div data-testid="jc-license-block">
              <span className="text-slate-500">Driver's Licence:</span>
              <p className="font-medium tracking-wide" data-testid="jc-customer-license">
                {jobCard.customer_license_number || '-'}
              </p>
            </div>
            <div data-testid="jc-address-block">
              <span className="text-slate-500">Address:</span>
              <p className="font-medium" data-testid="jc-customer-address">
                {[
                  jobCard.customer_address_line1,
                  jobCard.customer_address_line2,
                  jobCard.customer_suburb,
                  jobCard.customer_state,
                  jobCard.customer_postcode,
                ].filter(Boolean).join(', ') || '-'}
              </p>
            </div>
            {/* iter139.2 — Reference link to the source quoted invoice
                when this JC was created from a "Book Customer" flow on
                a previously quoted invoice. Lets SM see exactly what was
                quoted to the customer when discussing the booking. */}
            {jobCard.source_quote_invoice_id && (
              <div className="col-span-2 md:col-span-3" data-testid="jc-source-quote-invoice">
                <span className="text-slate-500 flex items-center gap-1">
                  <FileText className="h-3 w-3" />Quoted in:
                </span>
                <a
                  href={withSessionHash(`/invoices/${jobCard.source_quote_invoice_id}/edit`)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                  data-testid="jc-source-quote-invoice-link"
                >
                  View Quoted Invoice →
                </a>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Tasks below carry the pricing from this quote. New invoice will auto-fill.
                </p>
              </div>
            )}
            {/* iter158 — Reference link to the customer's PREVIOUS
                invoice for context ("what did we do last time?"). Fetched
                lazily on modal open. Excludes the current JC's own
                source_quote_invoice_id so we always land on the record
                BEFORE the current booking. Handles Boyce-imported
                historical invoices seamlessly. */}
            {lastInvoice && (
              <div className="col-span-2 md:col-span-3" data-testid="jc-last-invoice">
                <span className="text-slate-500 flex items-center gap-1">
                  <History className="h-3 w-3" />Last invoice for this customer:
                </span>
                <a
                  href={withSessionHash(`/invoices/${lastInvoice.invoice_id}/edit`)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-700 hover:text-indigo-900 font-semibold inline-flex items-center gap-1.5 mt-0.5"
                  data-testid="jc-last-invoice-link"
                  title={
                    lastInvoice.source === 'boyce'
                      ? 'Historical invoice imported from Boyce library'
                      : 'Opens the actual editable invoice, not a PDF snapshot'
                  }
                >
                  <span className="font-mono">{lastInvoice.doc_no || lastInvoice.invoice_id}</span>
                  {typeof lastInvoice.total === 'number' && (
                    <span className="text-slate-700 font-bold">${Number(lastInvoice.total).toFixed(2)}</span>
                  )}
                  {(lastInvoice.service_date || lastInvoice.invoice_date) && (
                    <span className="text-slate-500 text-[11px] font-normal">
                      {new Date(lastInvoice.service_date || lastInvoice.invoice_date)
                        .toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                  {lastInvoice.vehicle_rego && (
                    <span className="text-slate-500 text-[11px] font-normal uppercase">
                      · {lastInvoice.vehicle_rego}
                    </span>
                  )}
                  {lastInvoice.source === 'boyce' && (
                    <span
                      className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold"
                      data-testid="jc-last-invoice-boyce-badge"
                    >
                      Boyce
                    </span>
                  )}
                  <span aria-hidden>→</span>
                </a>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Opens the full editable invoice for reference — not a PDF.
                </p>
              </div>
            )}
            {/* Address — snapshotted from the customer doc at kiosk
                check-in or walk-in booking. Spans full row when present so
                long addresses don't get cut off. */}
            {/* iter216 — address now renders next to Driver's Licence above. */}
          </div>

          {/* Loan car requested — prominent banner so SM never misses it
              when the customer ticked the loan car checkbox at booking
              time OR confirmed it again at the kiosk. */}
          {jobCard.loan_car_requested && (
            <div
              className="flex items-center gap-2 rounded-md border border-cyan-300 bg-cyan-50 px-3 py-2 text-sm font-medium text-cyan-900"
              data-testid="jc-loan-car-banner"
            >
              <Key className="h-4 w-4" />
              Loan car requested — confirm availability before handing keys over.
            </div>
          )}

          {/* Customer concern from booking — surfaced prominently, editable
              by SM/FM so they can fix typos captured at check-in. The
              field itself stays as the customer's verbatim words (we
              never auto-translate). Each line can still be exploded into
              a JC task via "Add as tasks" (idempotent). */}
          {(canEdit || (jobCard.comments && jobCard.comments.trim())) && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/70 p-3"
              data-testid="jc-customer-notes"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-800 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Customer concern · from booking
                </p>
                <div className="flex items-center gap-2">
                  {commentsSavedAt && !commentsSaving && (
                    <span className="text-[10px] text-emerald-700">Saved</span>
                  )}
                  {commentsSaving && (
                    <span className="text-[10px] text-amber-700">Saving…</span>
                  )}
                  {canEdit && (commentsDraft || '').trim() && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddConcernsAsTasks}
                      disabled={addingConcerns}
                      data-testid="jc-add-concerns-as-tasks-btn"
                      className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                    >
                      <ListPlus className="h-3.5 w-3.5 mr-1" />
                      {addingConcerns ? 'Adding…' : 'Add as tasks'}
                    </Button>
                  )}
                </div>
              </div>
              {canEdit ? (
                <Textarea
                  value={commentsDraft}
                  onChange={(e) => setCommentsDraft(e.target.value)}
                  onBlur={saveComments}
                  placeholder="Customer concerns from the booking — edit if mis-keyed at check-in."
                  data-testid="jc-customer-concerns-edit"
                  className="bg-white border-amber-200 focus:border-amber-400 text-sm whitespace-pre-wrap min-h-[64px]"
                />
              ) : (
                <p className="text-sm text-slate-800 whitespace-pre-wrap">
                  {jobCard.comments || '—'}
                </p>
              )}
              <p className="text-[10px] text-slate-500 mt-1.5 italic">
                Each line becomes its own task for the mechanic to tick off.
              </p>
            </div>
          )}

          {/* ───── STEP 1 — Vehicle & Service Info ───── */}
          <Step1VehicleInfo
            canEdit={canEdit}
            vehRego={vehRego} setVehRego={setVehRego}
            vehYear={vehYear} setVehYear={setVehYear}
            vehMake={vehMake} setVehMake={setVehMake}
            vehModel={vehModel} setVehModel={setVehModel}
            odometer={odometer} setOdometer={setOdometer}
            serviceDate={serviceDate} setServiceDate={setServiceDate}
            nextSvcDate={nextSvcDate} setNextSvcDate={setNextSvcDate}
          />

          {/* iter147 — Show the most recent quote SMS body inline in the SM's
              context so they don't have to jump to Activity Log to remember
              what was quoted to the customer. Only rendered when a quote_sms_sent
              activity exists on this JC. */}
          {canManage && (() => {
            const quoteSmsEvents = (jobCard.activity_log || [])
              .filter((e) => e.event_type === 'quote_sms_sent')
              .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
            if (!quoteSmsEvents.length) return null;
            const latest = quoteSmsEvents[0];
            const remainingCount = quoteSmsEvents.length - 1;
            const sentDate = latest.timestamp ? new Date(latest.timestamp).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
            return (
              <section
                className="border border-indigo-200 rounded-md bg-indigo-50/40"
                data-testid="jc-quote-sms-history"
              >
                <header className="flex items-center gap-2 px-3 py-2 border-b border-indigo-200 bg-indigo-50 rounded-t-md">
                  <Send className="h-4 w-4 text-indigo-700" />
                  <h4 className="text-sm font-semibold text-indigo-900">Quote SMS sent to customer</h4>
                  <span className="ml-auto text-[11px] font-medium text-indigo-700">
                    {sentDate}
                    {remainingCount > 0 && ` · +${remainingCount} earlier`}
                  </span>
                </header>
                <div className="p-3">
                  <p
                    className="text-xs text-slate-800 whitespace-pre-wrap bg-white border border-indigo-100 rounded px-3 py-2"
                    data-testid="jc-quote-sms-body-preview"
                  >
                    {latest.body_preview || '(no body captured)'}
                  </p>
                  {Array.isArray(latest.recipients) && latest.recipients.length > 0 && (
                    <p className="text-[10px] text-slate-500 mt-1.5">
                      Sent to: {latest.recipients.join(', ')}
                    </p>
                  )}
                </div>
              </section>
            );
          })()}

          {/* iter138.2 — SM/FM-authored Notes section. Lives ABOVE the
              Tasks list so SM/FM can jot context (e.g. "customer wants
              under $400", "see service log entry from 12/05") without
              polluting the customer's verbatim booking comments. Each
              line can be promoted into its own JC task on demand via
              the idempotent "Add as tasks" button. Mechanic NEVER sees
              this block — internal-only by design. */}
          {canManage && (
            <section
              className="border border-blue-200 rounded-md bg-blue-50/40"
              data-testid="jc-sm-notes-section"
            >
              <header className="flex items-center gap-2 px-3 py-2 border-b border-blue-200 bg-blue-50 rounded-t-md">
                <NotebookPen className="h-4 w-4 text-blue-700" />
                <h4 className="text-sm font-semibold text-blue-900">SM / FM Notes</h4>
                <div className="ml-auto flex items-center gap-2">
                  {smNotesSavedAt && !smNotesSaving && (
                    <span className="text-[10px] text-emerald-700">Saved</span>
                  )}
                  {smNotesSaving && (
                    <span className="text-[10px] text-blue-700">Saving…</span>
                  )}
                  {(smNotesDraft || '').trim() && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddSmNotesAsTasks}
                      disabled={addingSmNotes}
                      data-testid="jc-add-sm-notes-as-tasks-btn"
                      className="h-7 text-xs border-blue-300 text-blue-800 hover:bg-blue-100"
                    >
                      <ListPlus className="h-3.5 w-3.5 mr-1" />
                      {addingSmNotes ? 'Adding…' : 'Add as tasks'}
                    </Button>
                  )}
                </div>
              </header>
              <div className="p-3">
                <Textarea
                  value={smNotesDraft}
                  onChange={(e) => setSmNotesDraft(e.target.value)}
                  onBlur={saveSmNotes}
                  placeholder="Internal SM / FM notes — won't show on the customer invoice. One thought per line; click 'Add as tasks' to push them into the Tasks list below."
                  data-testid="jc-sm-notes-edit"
                  className="bg-white border-blue-200 focus:border-blue-400 text-sm whitespace-pre-wrap min-h-[72px]"
                />
                <p className="text-[10px] text-slate-500 mt-1.5 italic">
                  Internal-only — mechanic / customer never see this. Save is auto on blur.
                </p>
              </div>
            </section>
          )}

          {/* ───── Quote sent to customer (SM/SuperAdmin only) ───── */}
          <JobCardQuoteSection
            jobCard={jobCard}
            user={user}
            onUpdate={(updated) => { updated && onUpdate && onUpdate(updated); }}
          />

          {/* ───── STEP 2 — Tasks (mechanic ticks them off) ───── */}
          <section className="border border-slate-200 rounded-md" data-testid="jc-step-2-tasks">
            <header className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 rounded-t-md">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-700 text-white text-xs font-bold">2</span>
              <Wrench className="h-4 w-4 text-slate-700" />
              <h4 className="text-sm font-semibold text-slate-800">Tasks · Tick when done</h4>
              <span className="text-[11px] text-slate-500 ml-auto">Completed tasks appear scratched in Step 4</span>
            </header>
            <div className="p-3">
              <JobCardLineItems
                jobCard={jobCard}
                user={user}
                onUpdated={(updated) => { onUpdate && onUpdate(updated); }}
              />
            </div>
          </section>

          {/* ───── STEP 3 — Mechanic Findings (+ photo evidence) ───── */}
          <section className="border border-amber-200 rounded-md" data-testid="jc-step-3-findings">
            <header className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-amber-50 rounded-t-md">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold">3</span>
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <h4 className="text-sm font-semibold text-amber-900">Mechanic Findings</h4>
              {(() => {
                // Pending = findings the mechanic still has to investigate.
                // Excludes any that were already promoted to a JC task via
                // the "Add Approved to Job Card" flow — matches what
                // MechanicFindingsList renders by default.
                const all = jobCard.findings_items || [];
                const pending = all.filter(f => !f.added_to_tasks_at);
                if (all.length === 0) return null;
                return (
                  <span className="text-[11px] font-normal text-amber-700" data-testid="findings-pending-count">
                    {pending.length} pending
                    {pending.length !== all.length && (
                      <span className="text-amber-600/70"> · {all.length - pending.length} moved to tasks</span>
                    )}
                  </span>
                );
              })()}
              <span className="text-[11px] text-amber-700/80 ml-auto">Take a photo with the camera button · SM can view photos in the menu</span>
            </header>
            <div className="p-3 space-y-2">
              <MechanicFindingsList
                jobCard={jobCard}
                canEdit={canEdit}
                isServiceManager={isServiceManager}
                onUpdated={(fresh) => onUpdate && onUpdate(fresh)}
              />
              {/* Legacy free-text notes — kept for backward compat. */}
              <div>
                <Label htmlFor="findings" className="text-xs text-slate-600">Additional free-form notes (optional)</Label>
                <Textarea
                  id="findings"
                  placeholder="ANYTHING THAT DOESN'T FIT A ROW…"
                  value={findings}
                  onChange={(e) => setFindings(e.target.value.toUpperCase())}
                  disabled={!canEdit}
                  className="mt-1 uppercase"
                  rows={2}
                  data-testid="jc-findings-textarea"
                />
              </div>
            </div>
          </section>

          {/* ───── STEP 4 — Quality Checklist ───── */}
          <Step4QualityChecklist
            jobCard={jobCard}
            checklist={checklist}
            checklistComplete={checklistComplete}
            canEdit={canEdit}
            nextServiceDate={nextServiceDate}
            handleChecklistChange={handleChecklistChange}
            handleNextServiceDateChange={handleNextServiceDateChange}
            onUpdate={onUpdate}
          />

          {/* ───── STEP 5 — Work Performed (completed tasks scratched here) ───── */}
          <Step5WorkPerformed
            jobCard={jobCard}
            canEdit={canEdit}
            canSMReview={canSMReview}
            isServiceManager={isServiceManager}
            saving={saving}
            workPerformed={workPerformed} setWorkPerformed={setWorkPerformed}
            hoursWorked={hoursWorked} setHoursWorked={setHoursWorked}
            derivedEstHours={derivedEstHours}
            actualHoursWorked={actualHoursWorked} setActualHoursWorked={setActualHoursWorked}
            derivedActualHours={derivedActualHours}
            hasOpenSession={hasOpenSession}
            handleSaveMechanicWork={handleSaveMechanicWork}
            onUpdate={onUpdate}
            readyToQuoteBusy={readyToQuoteBusy}
            onToggleReadyToQuote={handleToggleReadyToQuote}
          />

          {/* Submit for Review — for assigned mechanic. ALWAYS visible on
              active JCs so the mechanic has an obvious path forward. Click
              runs the detailed pre-checks and toasts contextual hints when
              something's still missing (no silent failure). */}
          {showSubmitButton && (
            <div
              className={`p-4 rounded-lg border ${
                canSubmitForReview
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-purple-50 border-purple-200'
              }`}
              data-testid="jc-submit-for-review"
            >
              <h4 className={`font-medium mb-2 ${
                canSubmitForReview ? 'text-emerald-800' : 'text-purple-800'
              }`}>
                {canSubmitForReview
                  ? 'Ready to submit for SM Review'
                  : 'Submit for SM Review when ready'}
              </h4>
              <p className={`text-sm mb-3 ${
                canSubmitForReview ? 'text-emerald-700' : 'text-purple-700'
              }`}>
                {canSubmitForReview
                  ? 'Quality Checklist is complete. Send this job card to the Service Manager for sign-off.'
                  : 'Click below to submit. If anything is missing (odo, rego, tasks, checklist, next service date) you\'ll see exactly what to fix.'}
              </p>
              {!actualHoursWorked || parseFloat(actualHoursWorked) <= 0 ? (
                <p className="text-sm text-amber-700 mb-2 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Tip: clock in via the kanban (or enter actual hours in Step 5) so the SM sees real hours worked.
                </p>
              ) : null}
              <Button
                onClick={handleSubmitForReview}
                disabled={submittingForReview}
                className={
                  canSubmitForReview
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }
                data-testid="jc-submit-for-review-btn"
              >
                {submittingForReview ? 'Submitting...' : 'Submit for SM Review →'}
              </Button>
            </div>
          )}

          {/* SM Review */}
          {canSMReview && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Service Manager Review</h4>
              <p className="text-sm text-blue-700 mb-3">
                Submitted by mechanic on {jobCard.submitted_for_review_at ? new Date(jobCard.submitted_for_review_at).toLocaleDateString() : 'N/A'}
              </p>
              <div className="mb-3">
                <Label className="text-sm text-blue-700">Actual Hours (editable)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={actualHoursWorked}
                  onChange={(e) => setActualHoursWorked(e.target.value)}
                  className="w-32 mt-1"
                />
              </div>
              <div className="mb-3">
                <Label className="text-sm text-blue-700">Review Notes</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes for mechanic or completion record..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleSMReview('approve')}
                  disabled={!!reviewAction}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="sm-approve-btn"
                >
                  {reviewAction === 'approve' ? 'Approving...' : 'Approve & Complete'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSMReview('send_back')}
                  disabled={!!reviewAction}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  data-testid="sm-send-back-btn"
                >
                  {reviewAction === 'send_back' ? 'Sending Back...' : 'Send Back to Mechanic'}
                </Button>
              </div>
            </div>
          )}

          {/* Sent-back notes for mechanic */}
          {jobCard.review_notes && jobCard.status === 'in_progress' && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-1">SM Review Notes</h4>
              <p className="text-sm text-amber-700">{jobCard.review_notes}</p>
            </div>
          )}

          {/* Billing — visible from Pickup stage onwards so SM can raise the
              invoice in advance instead of waiting for the mechanic to
              finish. Pre-existing post-completion paths still work. */}
          {isServiceManager && ['pickup', 'in_progress', 'awaiting_parts', 'sm_review', 'completed', 'delivered'].includes(jobCard.status) && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="jc-create-invoice-section">
              <h4 className="font-medium text-emerald-900 mb-2">Billing</h4>
              <p className="text-sm text-emerald-800 mb-3">
                {jobCard.invoice_id
                  ? 'This job card already has a linked invoice — open it to add ORP, RRP & supplier per line.'
                  : (
                    jobCard.status === 'pickup'
                      ? 'Bill ahead — create the invoice now from this booking. Tasks, quoted hours and customer details all pre-populate; mechanic can keep working while you finalise prices.'
                      : jobCard.status === 'in_progress' || jobCard.status === 'awaiting_parts'
                      ? 'Get a head start — create the invoice now while the mechanic works. Tasks + quoted hours pre-populate; you only need to add ORP / RRP / supplier per row.'
                      : 'Create the invoice draft. Customer, vehicle, line items & service date will pre-populate from this job card.'
                  )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={async () => {
                    // iter138.9m — When the JC already has a linked
                    // invoice, just open it. No need to POST /create-
                    // invoice (which IS idempotent on the BE but adds
                    // a roundtrip and a misleading toast for the user).
                    if (jobCard.invoice_id) {
                      openInNewTab(`/invoices/${jobCard.invoice_id}`);
                      return;
                    }
                    try {
                      const res = await fetch(
                        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/create-invoice`,
                        { method: 'POST', credentials: 'include' },
                      );
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(data.message);
                        if (data.invoice_id) {
                          // Anchor-click pattern keeps the JC modal open in
                          // the current tab while spawning the new tab.
                          openInNewTab(`/invoices/${data.invoice_id}`);
                          onUpdate && onUpdate();
                        }
                      } else {
                        const e = await res.json().catch(() => ({}));
                        toast.error(e.detail || 'Failed to create invoice');
                      }
                    } catch (_e) {
                      toast.error('Network error');
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="jc-create-invoice-btn"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {jobCard.invoice_id ? 'Open Linked Invoice' : 'Create Invoice from this JC'}
                </Button>
                {(jobCard.line_items || []).some(li => !li.completed) && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const date = prompt(
                        'Schedule follow-up job card for which date? (YYYY-MM-DD, Melbourne)',
                        new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('en-CA', { timeZone: 'Australia/Melbourne' }),
                      );
                      if (!date) return;
                      try {
                        const res = await fetch(
                          `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/clone-pending`,
                          {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ scheduled_date: date }),
                          },
                        );
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(`Follow-up JC ${data.job_card_number} scheduled — ${data.carried_over_tasks} task(s) carried`);
                        } else {
                          const e = await res.json().catch(() => ({}));
                          toast.error(e.detail || 'Failed to schedule follow-up');
                        }
                      } catch (_e) { toast.error('Network error'); }
                    }}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                    data-testid="jc-clone-pending-btn"
                  >
                    Schedule follow-up JC for pending tasks
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─────── Activity Log (append-only audit trail) ─────── */}
        <ActivityLogSection
          jobCard={jobCard}
          onUpdate={onUpdate}
          canManage={canManage}
        />

        <DialogFooter className="mt-6 flex-wrap gap-2">
          {isMechanic && jobCard.assigned_to === user?.user_id && jobCard.status !== 'completed' && (
            <Button
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                const comment = prompt('Briefly describe what needs the Service Manager\'s attention:');
                if (comment === null) return;
                try {
                  const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/escalate`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ comment }),
                  });
                  if (res.ok) {
                    toast.success('Escalated to Service Manager');
                    if (onUpdate) onUpdate();
                    onOpenChange(false);
                  } else toast.error('Failed to escalate');
                } catch (e) { toast.error('Failed to escalate'); }
              }}
              data-testid="escalate-job-btn"
            >
              ⚠ Escalate to Service Manager
            </Button>
          )}
          {jobCard.escalated && ['super_admin', 'service_manager', 'floor_manager'].includes(user?.role) && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/de-escalate`, {
                    method: 'POST', credentials: 'include',
                  });
                  if (res.ok) { toast.success('Cleared escalation'); if (onUpdate) onUpdate(); onOpenChange(false); }
                  else toast.error('Failed');
                } catch (e) { toast.error('Failed'); }
              }}
              data-testid="de-escalate-job-btn"
            >
              Clear escalation
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
      {/* Quick-edit dialogs — only mounted when open so they don't fetch
          on every JC modal render. onSaved triggers onUpdate so the JC
          re-fetches and shows the new denormalised values. */}
      <QuickEditCustomerDialog
        open={showEditCustomer}
        onOpenChange={setShowEditCustomer}
        customerId={resolvedCustomerId || jobCard.customer_id}
        jobCardId={jobCard.job_card_id}
        onSaved={onUpdate}
      />
      <QuickEditVehicleDialog
        open={showEditVehicle}
        onOpenChange={setShowEditVehicle}
        vehicleId={resolvedVehicleId || jobCard.vehicle_id}
        jobCardId={jobCard.job_card_id}
        onSaved={onUpdate}
      />
    </Dialog>
  );
}



/* ─────────────────────────────────────────────────────────────────────
 * ActivityLogSection
 * ---------------------------------------------------------------------
 * Append-only audit trail surfaced at the bottom of the JC modal. Lets
 * SM/SA review what changed, when, by whom, and the productivity dollar
 * impact at completion. Also exposes a Parts-Ordered toggle (visible on
 * active workflow states only) so SM can flip the supplier flag from
 * inside the modal AS WELL AS from the wallet tile.
 *
 * User request (Feb 2026): "ALL THESE INFORMATION ON JOB CARD TILE
 * CREATIVELY CAPTURE INSIDE JOB CARD AS WELL, SO WE TRACK THE ACTIONS
 * DONE. LIKE INSIDE JOB CARD AT THE BOTTOM, GIVE SECTION LOG, SO IT
 * CAPTURES ALL ACTIVITY DONE ON JOB CARD, LIKE TITLE UPDATED,
 * RESCHEDULED, MECHANIC ASSIGNED EVERYTIME CHANGED, UNTIL TO COMPELTED
 * WITH DOLLAR LOSS VALUE ALONG WITH ACTUAL HOURS PRODUCTIVITY."
 * ───────────────────────────────────────────────────────────────────── */
const EVENT_META = {
  status_change:       { Icon: ListPlus,    color: 'text-blue-700 bg-blue-50 border-blue-200',         label: 'Status' },
  mechanic_assigned:   { Icon: UserCog,     color: 'text-indigo-700 bg-indigo-50 border-indigo-200',   label: 'Mechanic' },
  rescheduled:         { Icon: CalendarClock,color: 'text-amber-700 bg-amber-50 border-amber-200',     label: 'Rescheduled' },
  parts_ordered:       { Icon: PackageCheck,color: 'text-emerald-700 bg-emerald-50 border-emerald-200',label: 'Parts' },
  parts_unordered:     { Icon: Package,     color: 'text-rose-700 bg-rose-50 border-rose-200',         label: 'Parts' },
  details_edited:      { Icon: Pencil,      color: 'text-slate-700 bg-slate-50 border-slate-200',      label: 'Edited' },
  priority_reordered:  { Icon: TrendingUp,  color: 'text-violet-700 bg-violet-50 border-violet-200',   label: 'Priority' },
};

function fmtEventTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  } catch (_e) { return iso; }
}

function ActivityLogSection({ jobCard, onUpdate, canManage }) {
  const [busy, setBusy] = useState(false);
  const entries = useMemo(() => {
    const raw = Array.isArray(jobCard.activity_log) ? [...jobCard.activity_log] : [];
    // Newest first for review-at-a-glance.
    return raw.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  }, [jobCard.activity_log]);

  // Parts toggle — SM/admin/super_admin only.
  const showPartsToggle = canManage
    && ['pickup', 'in_progress', 'waiting_for_parts', 'blocked'].includes(jobCard.status);

  const onTogglePartsClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/toggle-parts-ordered`,
        { method: 'POST', credentials: 'include' },
      );
      if (r.ok) {
        const d = await r.json();
        toast.success(d.parts_ordered ? 'Parts marked as ORDERED' : 'Parts flag cleared');
        if (onUpdate) onUpdate();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Could not update parts status');
      }
    } catch (_e) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="jc-activity-log">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <History className="h-4 w-4 text-slate-600" />
        <h3 className="text-sm font-bold text-slate-900">Activity Log</h3>
        <span className="text-[11px] text-slate-500">({entries.length} event{entries.length !== 1 ? 's' : ''})</span>

        {/* Parts-ordered toggle inside the modal as well */}
        {showPartsToggle && (
          <button
            type="button"
            disabled={busy}
            onClick={onTogglePartsClick}
            data-testid="jc-parts-ordered-toggle"
            className={`
              ml-auto inline-flex items-center gap-1.5 text-xs font-bold uppercase
              px-3 py-1.5 rounded-md border transition-colors
              ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
              ${jobCard.parts_ordered
                ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                : 'bg-rose-500 text-white border-rose-600 hover:bg-rose-600 animate-pulse'}
            `}
          >
            {jobCard.parts_ordered
              ? (<><PackageCheck className="h-3.5 w-3.5" /> Parts Ordered</>)
              : (<><Package className="h-3.5 w-3.5" /> Order Parts</>)}
          </button>
        )}
      </div>

      {jobCard.parts_ordered && jobCard.parts_ordered_at && (
        <p className="text-[11px] text-emerald-700 mb-2 flex items-center gap-1">
          <PackageCheck className="h-3 w-3" />
          Parts ordered on {fmtEventTime(jobCard.parts_ordered_at)}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="text-xs text-slate-400 italic">
          No activity logged yet — events will appear here as the job progresses.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {entries.map((e) => {
            const meta = EVENT_META[e.event_type]
              || { Icon: History, color: 'text-slate-700 bg-slate-50 border-slate-200', label: 'Event' };
            const Icon = meta.Icon;
            const md = e.metadata || {};
            const isCompletion = e.event_type === 'status_change' && md.to === 'completed';
            const deltaAud = Number(md.delta_aud || 0);
            return (
              <div
                key={e.event_id}
                className={`flex gap-2 text-[12px] border rounded px-2 py-1.5 ${meta.color}`}
                data-testid={`jc-activity-${e.event_id}`}
              >
                <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{e.description}</p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {fmtEventTime(e.timestamp)}
                    {e.actor_name && (<span> · {e.actor_name}</span>)}
                  </p>
                  {/* Completion event — surface productivity stats prominently */}
                  {isCompletion && (md.quoted_hours != null || md.actual_hours != null) && (
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono text-slate-700">
                        Quoted {Number(md.quoted_hours || 0).toFixed(1)}h
                        · Actual {Number(md.actual_hours || 0).toFixed(1)}h
                      </span>
                      {deltaAud > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700">
                          <TrendingDown className="h-3 w-3" />
                          -${deltaAud.toFixed(0)} loss
                        </span>
                      )}
                      {deltaAud < 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                          <TrendingUp className="h-3 w-3" />
                          ${Math.abs(deltaAud).toFixed(0)} saved
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
